'use client';

import { Fragment, useCallback } from 'react';
import { FormDivider } from '@/ui/common/Dividers/FormDivider';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { formatTicketDuration } from '@/service/utils/formatTicketDuration';
import { TTicketId } from '@/domain/FullCalendar.domain';
import { RentalCounter } from '../AttractionsContent/core/RentalCounter';

type TTicketsCounterProps = {
    tickets: TTicketId[] | undefined;
    clearBackError: () => void;
    needTicketNames: boolean;
    maxTicketsSelected: boolean;
};

export const TicketsCounter = ({
    tickets,
    clearBackError,
    needTicketNames,
    maxTicketsSelected,
}: TTicketsCounterProps) => {
    const {
        selectedTickets,
        selectTicket,
        ticketNames,
        addTicketNames,
        removeTicketNames,
        changeTicketNames,
    } = useAvailabilityStore();

    const handleTicketsCountChange = useCallback(
        (ticket_id: number, ticket_count: number) => {
            selectTicket(ticket_id, ticket_count);
            clearBackError();
        },
        [clearBackError, selectTicket]
    );

    return (
        <div className="mx-auto flex max-w-full flex-col items-center [&>*:nth-child(1)]:border-none">
            {tickets?.map(
                (
                    { id, name, price, remainToSelect, avail, duration, description, min },
                    index,
                    array
                ) => (
                    <Fragment key={id}>
                        <RentalCounter
                            id={id.toString()}
                            name={name}
                            duration={formatTicketDuration(duration)}
                            price={price}
                            count={selectedTickets[id] ?? 0}
                            min={+min}
                            handleCount={(count) => handleTicketsCountChange(id, count)}
                            needTicketNames={needTicketNames}
                            ticketNames={ticketNames}
                            handleTicketNamesAdd={addTicketNames}
                            handleTicketNamesRemove={removeTicketNames}
                            handleTicketNamesChange={changeTicketNames}
                            infoText={description}
                            notAllowed={
                                maxTicketsSelected ||
                                (remainToSelect && remainToSelect <= 0) ||
                                avail === selectedTickets[id] ||
                                false
                            }
                            notAllowedText="For this tour, you are required to choose one option. If you select this option, please make sure to deselect any others."
                        />
                        {index !== array.length - 1 && <FormDivider className="mb-2 mt-5" />}
                    </Fragment>
                )
            )}
        </div>
    );
};
